// DatabaseSettingsMissionRow.jsx

import React from 'react';

// Context
import { useTheme } from 'context/ThemeContext';

// CSS
import './DatabaseSettingsMissionRow.css';

// Icons
import Icon from 'svg/Icon';

/**
 * DatabaseSettingsMissionRow
 *
 * Renders one draggable Mission task row in the database settings Missions tab.
 *
 * @param {boolean} isDragging - Whether the row is currently being dragged.
 * @param {boolean} isDropTarget - Whether the row is the active drop target.
 * @param {string} label - Primary task title.
 * @param {string} note - Secondary task summary.
 * @param {function} onClick - Opens the Mission task editor.
 * @param {function} onDragEnd - Clears drag state.
 * @param {function} onDragOver - Handles drag-over events.
 * @param {function} onDragStart - Starts dragging.
 * @param {function} onDrop - Handles drop.
 * @param {function} onSettingsClick - Opens the Mission task editor via the settings action.
 *
 * @returns {ReactNode} The rendered Mission task row.
 */
const DatabaseSettingsMissionRow = React.memo(({
  isDragging,
  isDropTarget,
  label,
  note,
  onClick,
  onDragEnd,
  onDragOver,
  onDragStart,
  onDrop,
  onSettingsClick,
}) => {

  // ----- CONTEXT ----- //

  const { theme } = useTheme();

  // ----- RENDER ----- //

  return (
    <div
      className={`database-settings-mission-row-container${isDragging ? ' dragging' : ''}`}
      style={{
        backgroundColor: isDropTarget ? theme.backgroundHover : theme.panel,
        borderColor: isDropTarget ? theme.foregroundMuted : theme.backgroundHover,
      }}
      draggable
      title={label || 'Mission Task'}
      onClick={onClick}
      onDragEnd={onDragEnd}
      onDragOver={onDragOver}
      onDragStart={onDragStart}
      onDrop={onDrop}
    >

      {/* MAIN */}
      <div className="database-settings-mission-row-main">

        {/* ICON */}
        <div className="database-settings-mission-row-icon">
          <Icon
            color={theme.foregroundMuted}
            height={18}
            iconKey="assignment"
            width={18}
          />
        </div>

        {/* COPY */}
        <div className="database-settings-mission-row-copy">

          {/* LABEL */}
          <div
            className="database-settings-mission-row-label"
          >
            {label}
          </div>

          {/* NOTE */}
          {note ? (

            <div
              className="database-settings-mission-row-note"
            >
              {note}
            </div>
          ) : null}
        </div>
      </div>

      {/* ACTIONS */}
      <div className="database-settings-mission-row-actions">

        {/* ACTION */}
        <button
          className="database-settings-mission-row-action"
          title="Edit mission task"
          type="button"
          onClick={(event) => {
            event.stopPropagation();
            onSettingsClick?.();
          }}
        >
          <Icon
            color={theme.foregroundMuted}
            height={16}
            iconKey="settings"
            width={16}
          />
        </button>

        {/* DRAG HANDLE */}
        <div className="database-settings-mission-row-drag-handle" title="Drag to reorder">
          <Icon
            color={theme.foregroundMuted}
            height={18}
            iconKey="drag_indicator"
            width={18}
          />
        </div>
      </div>
    </div>
  );
});

// Dev Tools
DatabaseSettingsMissionRow.displayName = 'DatabaseSettingsMissionRow';

export default DatabaseSettingsMissionRow;
